import { Transform } from 'class-transformer';
import {
	IsArray,
	IsBoolean,
	IsIn,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	Matches,
	MaxLength,
} from 'class-validator';
import { sanitizeFieldKey } from './custom-fields.dto';
import type { PublicStandardFieldDto } from './registration-standard-field.dto';

// Field types a registration question can be. Kept as a peer/subset of
// CUSTOM_FIELD_TYPES so the shared CustomFieldEditor renderer + validation work
// unchanged for registration fields.
export const REGISTRATION_FIELD_TYPES = [
	'text',
	'textarea',
	'number',
	'date',
	'datetime',
	'boolean',
	'select',
	'multiselect',
	'phone',
	'email',
	'url',
] as const;

export type RegistrationFieldType = (typeof REGISTRATION_FIELD_TYPES)[number];

export class CreateRegistrationFieldDto {
	// Null/omitted = tenant-wide default; a portal_site id = override for that
	// storefront.
	@IsNumber()
	@IsOptional()
	public portalSiteId?: number | null;

	@IsString()
	@Transform(({ value }) => sanitizeFieldKey(value))
	@Matches(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/, {
		message:
			'Field key must start with a letter and contain only letters, numbers, and underscores (max 60 chars)',
	})
	@MaxLength(60)
	public fieldKey!: string;

	@IsString()
	@MaxLength(255)
	public label!: string;

	@IsString()
	@IsIn(REGISTRATION_FIELD_TYPES)
	public fieldType!: RegistrationFieldType;

	@IsString()
	@IsOptional()
	@MaxLength(255)
	public placeholder?: string | null;

	@IsString()
	@IsOptional()
	@MaxLength(500)
	public helpText?: string | null;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsBoolean()
	@IsOptional()
	public includeInEmail?: boolean;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;
}

export class UpdateRegistrationFieldDto {
	@IsString()
	@MaxLength(255)
	@IsOptional()
	public label?: string;

	@IsString()
	@IsIn(REGISTRATION_FIELD_TYPES)
	@IsOptional()
	public fieldType?: RegistrationFieldType;

	@IsString()
	@IsOptional()
	@MaxLength(255)
	public placeholder?: string | null;

	@IsString()
	@IsOptional()
	@MaxLength(500)
	public helpText?: string | null;

	@IsBoolean()
	@IsOptional()
	public isActive?: boolean;

	@IsBoolean()
	@IsOptional()
	public isRequired?: boolean;

	@IsBoolean()
	@IsOptional()
	public includeInEmail?: boolean;

	@IsString()
	@IsOptional()
	public defaultValue?: string | null;

	@IsObject()
	@IsOptional()
	public validationRules?: Record<string, unknown> | null;

	@IsNumber()
	@IsOptional()
	public sortOrder?: number;

	@IsArray()
	@IsOptional()
	public selectOptions?: Array<{ value: string; label: string }> | null;
}

// Full definition as returned to admin/config surfaces.
export interface RegistrationFieldDefinitionDto {
	id: number;
	portalSiteId: number | null;
	fieldKey: string;
	label: string;
	fieldType: string;
	placeholder: string | null;
	helpText: string | null;
	isRequired: boolean;
	isActive: boolean;
	includeInEmail: boolean;
	defaultValue: string | null;
	validationRules: Record<string, unknown> | null;
	sortOrder: number;
	selectOptions: Array<{ value: string; label: string }> | null;
	createdDate: Date;
	modifiedDate: Date;
}

// Trimmed shape the PUBLIC storefront needs to RENDER a field (no audit / scope
// internals). Returned by the public registration-form config endpoint
// (PD-14330). Derived from the definition so the types can't drift.
export type PublicRegistrationFieldDto = Pick<
	RegistrationFieldDefinitionDto,
	| 'fieldKey'
	| 'label'
	| 'fieldType'
	| 'placeholder'
	| 'helpText'
	| 'isRequired'
	| 'sortOrder'
	| 'selectOptions'
>;

// Response of the public registration-form config endpoint
// (GET /api/portal/registration-form) the storefront renders from. `fields` are
// the admin-configured supplemental questions; `standardFields` is the
// visibility/required config for the built-in standard fields (PD-14370).
export type GetPublicRegistrationFormResponseDto =
	| {
			success: true;
			fields: Array<PublicRegistrationFieldDto>;
			standardFields: Array<PublicStandardFieldDto>;
	  }
	| { success: false; error: 'tenant_not_found' | 'registration_disabled' };

// Scope option for the admin builder (a tenant storefront).
export interface RegistrationFieldPortalDto {
	id: number;
	name: string;
	alias: string;
}

export type GetRegistrationFieldPortalsResponseDto =
	| { success: true; data: Array<RegistrationFieldPortalDto> }
	| { success: false; error: string };

export type GetRegistrationFieldsResponseDto =
	| { success: true; data: Array<RegistrationFieldDefinitionDto> }
	| { success: false; error: string };

export type MutateRegistrationFieldResponseDto =
	| { success: true; data: RegistrationFieldDefinitionDto }
	| { success: false; error: string };

export type DeleteRegistrationFieldResponseDto =
	| { success: true; message: string }
	| { success: false; error: string };
